<template>
    <div>
        <div style="position: relative;">
            <label v-if="label" class="editor-label">{{ label }}</label>
            <ckeditor :editor="editor" v-model="content" @ready="onReady" @input="updateModel" :config="editorConfig"></ckeditor>
        </div>
        <p
            v-if="hint && (persistentHint || !errorMessages)"
            class="editor-hint"
        >
            {{ hint }}
        </p>
        <p v-if="errorMessages" class="error-messages">{{ errorMessages}}</p>
    </div>
</template>

<script>
import CKEditor from '@ckeditor/ckeditor5-vue2';
import DecoupledEditor from '@ckeditor/ckeditor5-build-decoupled-document/build/ckeditor';
import notifications from "../mixins/notifications";
import UploadAdapterPlugin from '../editor/CKEditor/UploadAdapter';
import { pickFromFileManager } from '../editor/CKEditor/FileManager';

export default {
    name: "EditorComponent",
    components: {
        ckeditor: CKEditor.component
    },
    mixins: [notifications],
    props: {
        value: {
            type: String,
            default: ''
        },
        errorMessages: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        plainTextPaste: {
            type: Boolean,
            default: true
        },
        height: {
            type: String,
            default: '400px'
        },
        toolbar: {
            type: Object,
            default: () => {
                return {
                    items: [
                        'undo', 'redo',
                        '|', 'heading',
                        '|', 'fontfamily', 'fontsize', 'fontColor', 'fontBackgroundColor',
                        '|', 'bold', 'italic', 'strikethrough', 'subscript', 'superscript', 'code',
                        '|', 'alignment',
                        'link', 'blockQuote', 'codeBlock',
                        '|', 'bulletedList', 'numberedList', 'todoList', 'outdent', 'indent'
                    ],
                    shouldNotGroupWhenFull: true
                }
            }
        },
        label: {
            type: String,
            default: ''
        },
        hint: {
            type: String,
            default: ''
        },
        persistentHint: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            editor: DecoupledEditor,
            content: '',
            editorInstance: null,
            wrappedOnce: false,
        }
    },
    computed: {
        editorConfig() {
            return {
                extraPlugins: [UploadAdapterPlugin],
                language: 'en',
                contentLanguage: 'en',
                toolbar: this.toolbar,
                link: {
                    decorators: [
                        {
                            mode: 'manual',
                            label: 'Open in a new tab',
                            defaultValue: true,
                            attributes: {
                                target: '_blank',
                                rel: 'noopener noreferrer'
                            }
                        }
                    ]
                },
                mediaEmbed: {
                    previewsInData: true
                }
            }
        }
    },
    watch: {
        value(value) {
            this.content = value ?? '';
        },
        height(newHeight) {
            if (this.editorInstance) {
                this.setEditorHeight(this.editorInstance, newHeight);
            }
        },
        disabled(value) {
            this.setReadOnlyMode(value);
        }
    },
    mounted() {
        this.content = this.value ?? '';
    },
    methods: {
        updateModel() {
            this.$emit('update:value', this.content);
        },
        onReady(editor) {
            this.editorInstance = editor;
            const originalGetData = editor.getData.bind(editor);

            editor.getData = () => {
                const raw = originalGetData();
                return this.wrap(raw);
            };

            editor.ui.autofocus = false;
            const editable = editor.ui.getEditableElement();
            const toolbar  = editor.ui.view.toolbar.element;
            editable.parentElement.insertBefore(toolbar, editable);

            this.addToolbarButton(toolbar);
            this.setEditorHeight(editor, this.height);

            if (this.plainTextPaste) {
                this.enablePlainTextPaste(editor);
            }

            this.setReadOnlyMode(this.disabled);
        },
        addToolbarButton(toolbar) {
            const items = toolbar.querySelector('.ck-toolbar__items');
            const btn = document.createElement('button');

            btn.type = 'button';
            btn.className = 'ck ck-button';
            btn.title = 'Library';
            btn.setAttribute('data-fm-button', 'true');
            btn.innerHTML = '<span class="ck ck-button__inner"><span class="image-icon"></span></span>';
            btn.addEventListener('click', this.openFileManager);

            items.appendChild(btn);
        },
        async openFileManager() {
            const url = await pickFromFileManager({ type: 'image' });
            if (!url) return;

            this.editorInstance.editing.view.focus();
            this.editorInstance.plugins.get('ImageUtils').insertImage({ src: url });
        },
        setEditorHeight(editor, height) {
            editor.editing.view.change(writer => {
                writer.setStyle('min-height', height, editor.editing.view.document.getRoot());
            });
        },
        setReadOnlyMode(readOnly) {
            if (!this.editorInstance) {
                return;
            }

            if (readOnly) {
                this.editorInstance.enableReadOnlyMode('EditorComponent');
            } else {
                this.editorInstance.disableReadOnlyMode('EditorComponent');
            }

            const libraryButton = this.editorInstance.ui.view.toolbar.element.querySelector('[data-fm-button]');

            if (libraryButton) {
                libraryButton.disabled = readOnly;
                libraryButton.setAttribute('aria-disabled', readOnly.toString());
            }
        },
        enablePlainTextPaste(editor) {
            editor.editing.view.document.on('clipboardInput', (event, data) => {
                const plainText = data.dataTransfer.getData('text/plain');
                if (plainText) {
                    event.stop();
                    editor.model.change(writer => {
                        editor.model.insertContent(writer.createText(plainText));
                    });
                }
            });
        },
        wrap(rawHtml) {
            return `<div class="ck-content">${rawHtml}</div>`;
        },
    }
}
</script>

<style>
.ck-editor__editable {
    border: 1px solid #ccced1 !important;
    border-radius: 2px !important;
}

.ck-content:after, .ck-content:before {
    content: none !important;
}

.ck-powered-by {
    display: none !important;
}

.error-messages {
    color: #ff5252 !important;
    font-size: 12px;
}

.ck .image-icon {
    width: 16px;
    height: 16px;
    display: inline-block;
    background-color: currentColor;
    -webkit-mask: url("/admin/fontawesome/svgs/solid/image.svg") no-repeat center / 16px 16px;
    mask: url("/admin/fontawesome/svgs/solid/image.svg") no-repeat center / 16px 16px;
}

.editor-label {
    display: block;
    margin-bottom: 4px;
    font-size: 14px;
    font-weight: 500;
}

.editor-hint {
    margin-top: 4px;
    font-size: 12px;
    color: #6c757d;
}
</style>
